import uuid from 'uuid-random';
import { LANGUAGE, TEXT_TYPE } from './../../constants/index';
import {
  convertFromRaw,
  convertToRaw,
  DraftInlineStyleType,
  EditorState,
  RawDraftContentBlock,
  RawDraftContentState,
} from 'draft-js';
import { HeadingTool } from '../../components/EditTool/TextEditor/HeadingTool';
import { parseWordsAndSymbols } from './common';
import { serialNumberGenerator } from '../generators';
import { ContentLine, LaterExemplar, ExemplarWord, Image, Section, ProcedureContentLine } from '../../types';
import {
  LaterTextEditorSection,
  LaterTextEditorSideState,
  LaterTextEditorToolbarConfig,
  TextEditorToolbar,
} from '../../types/textEditor';
import { PHRASES, PREPOSITIONAL_PHRASES } from '../../constants';
import {
  BLOCK_TYPES,
  DEFAULT_STYLES,
  LINE_BREAKS,
  SEPARATE_NEXT,
  SEPARATE_NEXT_PHRASE,
} from '../../constants/textEditor';
import BoldIcon from '../../assets/bold-tool.png';
import ItalicIcon from '../../assets/italic-tool.png';
import UnorderedListIcon from '../../assets/unordered-list-tool.png';
import OrderedListIcon from '../../assets/ordered-list-tool.png';
import UndoIcon from '../../assets/undo-tool.png';
import RedoIcon from '../../assets/redo-tool.png';
import { handleFlaggedWords, handleHighlight, isHighlighted } from './textEditor';

export const getLaterTextEditorToolbar = (toolbarConfig?: LaterTextEditorToolbarConfig): TextEditorToolbar => {
  const { showHeading = false, showList = false } = toolbarConfig || {};

  const toolbarOptions = {
    options: ['inline', 'history'],
    inline: {
      options: ['bold', 'italic'],
      bold: { icon: BoldIcon, className: 'text-editor-tool' },
      italic: { icon: ItalicIcon, className: 'text-editor-tool' },
    },
    blockType: {
      inDropdown: false,
      options: ['H3'],
      component: HeadingTool,
    },
    list: {
      options: ['unordered', 'ordered'],
      unordered: { icon: UnorderedListIcon, className: 'text-editor-tool sm-img' },
      ordered: { icon: OrderedListIcon, className: 'text-editor-tool sm-img' },
    },
    history: {
      inDropdown: false,
      options: ['undo', 'redo'],
      undo: { icon: UndoIcon, className: 'text-editor-tool' },
      redo: { icon: RedoIcon, className: 'text-editor-tool' },
    },
  };

  if (showHeading) {
    toolbarOptions.options.splice(1, 0, 'blockType');
  }

  if (showList) {
    toolbarOptions.options.splice(2, 0, 'list');
  }

  return toolbarOptions;
};

export const getLaterEditorLineFromWords = (
  words: ExemplarWord[],
  label: string,
  styles?: string[]
): RawDraftContentBlock => {
  const isTitle = label === 'Title';
  const isSubHeading = !!styles?.includes('sub-heading');
  const isUnorderedList = !!styles?.includes('unordered-list');
  const isOrderedList = !!styles?.includes('ordered-list');

  let editorBlock: RawDraftContentBlock = {
    type: BLOCK_TYPES.unStyled,
    key: uuid(),
    depth: 0,
    inlineStyleRanges: [],
    entityRanges: [],
    data: {},
    text: '',
  };

  if (isTitle) {
    editorBlock.type = BLOCK_TYPES.headerTwo;
  } else if (isSubHeading) {
    editorBlock.type = BLOCK_TYPES.headerThree;
  } else if (isUnorderedList) {
    editorBlock.type = BLOCK_TYPES.unorderedListItem;
  } else if (isOrderedList) {
    editorBlock.type = BLOCK_TYPES.orderedListItem;
  }

  // eslint-disable-next-line complexity
  words.map(word => {
    const isBold = word.style?.includes('bold');
    const isItalic = word.style?.includes('italic');
    const isNospacing = word.style?.includes('nospacing');
    const isSeparateNext = word.style?.includes('separate-next');
    const isSeparateNextPhrase = word.style?.includes('separate-next-phrase');
    const isPartOfSpeech = word.hasDefaultLanguage || word.language;
    const isPhrase = word.phrase;
    const isPrepositionalPhrase = !!word.prepositional_phrase;
    const containsTextType = word.text_type;
    const isSpaceNoNeeded =
      isNospacing || !editorBlock.text.length || editorBlock.text[editorBlock.text.length - 1] === '\n';
    const styleOffset = isSpaceNoNeeded ? 0 : 1;

    if (LINE_BREAKS.includes(word.text)) {
      editorBlock = {
        ...editorBlock,
        text: `${editorBlock.text}\n`,
      };
      return;
    }

    isItalic &&
      editorBlock.inlineStyleRanges.push({
        offset: editorBlock.text.length + styleOffset,
        length: word.text.length,
        style: 'ITALIC',
      });

    isBold &&
      editorBlock.inlineStyleRanges.push({
        offset: editorBlock.text.length + styleOffset,
        length: word.text.length,
        style: 'BOLD',
      });

    isSeparateNext &&
      editorBlock.inlineStyleRanges.push({
        offset: editorBlock.text.length + styleOffset,
        length: word.text.length,
        style: JSON.stringify({ text: word.text, flag: SEPARATE_NEXT }) as DraftInlineStyleType,
      });

    isSeparateNextPhrase &&
      editorBlock.inlineStyleRanges.push({
        offset: editorBlock.text.length + styleOffset,
        length: word.text.length,
        style: JSON.stringify({ text: word.text, flag: SEPARATE_NEXT_PHRASE }) as DraftInlineStyleType,
      });

    if (isPartOfSpeech) {
      let subwordOffset = styleOffset;
      word.text.split(' ').map(subword => {
        editorBlock.inlineStyleRanges.push({
          offset: editorBlock.text.length + subwordOffset,
          length: subword.length,
          style: JSON.stringify({ text: subword, flag: LANGUAGE, value: word.language }) as DraftInlineStyleType,
        });
        subwordOffset += subword.length + 1;
      });
    }

    if (isPrepositionalPhrase) {
      let subwordOffset = styleOffset;
      word.text.split(' ').map(subword => {
        editorBlock.inlineStyleRanges.push({
          offset: editorBlock.text.length + subwordOffset,
          length: subword.length,
          style: JSON.stringify({ text: subword, flag: PREPOSITIONAL_PHRASES }) as DraftInlineStyleType,
        });
        subwordOffset += subword.length + 1;
      });
    }

    if (isPhrase) {
      let subwordOffset = styleOffset;
      word.text.split(' ').map(subword => {
        editorBlock.inlineStyleRanges.push({
          offset: editorBlock.text.length + subwordOffset,
          length: subword.length,
          style: JSON.stringify({ text: subword, flag: PHRASES }) as DraftInlineStyleType,
        });
        subwordOffset += subword.length + 1;
      });
    }

    isHighlighted(word) && handleHighlight(word, editorBlock, styleOffset);

    if (containsTextType) {
      let subwordOffset = styleOffset;
      word.text.split(' ').map(subword => {
        editorBlock.inlineStyleRanges.push({
          offset: editorBlock.text.length + subwordOffset,
          length: subword.length,
          style: JSON.stringify({ text: subword, flag: TEXT_TYPE, value: word.text_type }) as DraftInlineStyleType,
        });
        subwordOffset += subword.length + 1;
      });
    }

    editorBlock = {
      ...editorBlock,
      text: `${editorBlock.text}${isSpaceNoNeeded ? '' : ' '}${word.text || ''}`,
    };
  });

  return editorBlock;
};

export const getLaterEditorStateFromExemplarLines = (section: Section): RawDraftContentState => {
  const editorState: RawDraftContentState = {
    entityMap: {},
    blocks: [],
  };

  if ('lines' in section) {
    section.lines.map(line => {
      editorState.blocks.push(
        getLaterEditorLineFromWords(line.words, (section as Section).label || '', (line as ProcedureContentLine).style)
      );
    });
  }

  return editorState;
};

export const getLaterEditorStateFromSide = (side: Array<Section | Image>): LaterTextEditorSideState => {
  return side.reduce((lines, section) => {
    if (section.type === 'section') {
      return {
        ...lines,
        [section.id as string]: {
          label: 'label' in section ? section.label : '',
          value: EditorState.createWithContent(
            convertFromRaw(getLaterEditorStateFromExemplarLines(section as Section))
          ),
          meta: {
            ...(Array.isArray(section.style) && { style: section.style }),
            ...(section.color && { color: section.color }),
          },
        },
      };
    }

    return lines;
  }, {});
};

const getLaterLineFromEditorBlock = (
  editorBlock: RawDraftContentBlock,
  sectionId: string,
  sectionIndex: number,
  lineIndex: number,
  contentSide: string,
  sequenceNumberGenerator: Generator
): ContentLine => {
  const line: ContentLine & ProcedureContentLine = {
    id: editorBlock.key,
    words: [],
  };

  if (!editorBlock.text.length) {
    return line;
  }

  if (editorBlock.type === BLOCK_TYPES.headerThree) {
    line.style = ['sub-heading'];
  } else if (editorBlock.type === BLOCK_TYPES.unorderedListItem) {
    line.style = ['unordered-list'];
  } else if (editorBlock.type === BLOCK_TYPES.orderedListItem) {
    line.style = ['ordered-list'];
    line.sequence_number = `${sequenceNumberGenerator.next().value}.`;
  }

  let currentOffset = 0;
  const arrayOfWords: Array<string> = parseWordsAndSymbols(editorBlock.text);

  arrayOfWords
    .filter(word => word === '\n' || word.trim())
    .map((word: string, index: number) => {
      const defaultStyles: string[] = [];
      const flaggedWords: string[] = [];
      const newWord: ExemplarWord = {
        id: uuid(),
        lineId: editorBlock.key as string,
        text: word === '\n' ? '\n\n' : word,
        wordId: uuid(),
        wordIndex: index,
        sectionId: sectionId,
        sectionIndex,
        lineIndex,
        side: contentSide,
      };

      const currentOffsetEnd = currentOffset + word.length - 1;

      editorBlock.inlineStyleRanges.map(style => {
        const styleOffset = style.offset;
        const styleOffsetEnd = style.offset + style.length - 1;

        if (
          (currentOffset >= styleOffset && currentOffset <= styleOffsetEnd) ||
          (currentOffset <= styleOffset && currentOffsetEnd >= styleOffset)
        ) {
          const isDefault = DEFAULT_STYLES.some(defaultStyle => {
            return style?.style.toLowerCase() === defaultStyle;
          });

          if (isDefault) {
            defaultStyles.push(style.style.toLowerCase());
            return;
          }

          flaggedWords.push(style.style);
        }

        return;
      });

      handleFlaggedWords(flaggedWords, newWord, word, defaultStyles);

      if (index > 0 && ![' ', '\n'].includes(editorBlock.text[currentOffset - 1])) {
        defaultStyles.push('nospacing');
      }

      if (defaultStyles.length) {
        newWord.style = defaultStyles;
      }

      currentOffset += word.length + (editorBlock.text[currentOffsetEnd + 1] === ' ' ? 1 : 0);

      line.words.push(newWord);
    });

  return line;
};

export const getLaterSectionFromEditorSide = (
  editorSection: LaterTextEditorSection,
  sectionIndex: number,
  contentSide: string,
  sequenceNumberGenerator: Generator
): Section => {
  const section: Section = {
    ...editorSection.meta,
    type: 'section',
    label: editorSection.label,
    lines: [],
    id: uuid(),
  };

  const rowEditorSection = convertToRaw(editorSection.value.getCurrentContent());
  rowEditorSection.blocks.map((block, lineIndex) => {
    section.lines.push(
      getLaterLineFromEditorBlock(block, section.id, sectionIndex, lineIndex, contentSide, sequenceNumberGenerator)
    );
  });

  return section;
};

export const getLaterSideFromEditorState = (
  editorStateSideContent: LaterTextEditorSideState,
  contentSide: string,
  sequenceNumberGenerator: Generator
): Section[] => {
  const side: Section[] = [];

  Object.entries(editorStateSideContent).map(([, editorSection], index) => {
    side.push(getLaterSectionFromEditorSide(editorSection, index, contentSide, sequenceNumberGenerator));
  });

  return side;
};

export const getNewLaterFromSides = (
  exemplar: LaterExemplar,
  leftContent: LaterTextEditorSideState,
  rightContent: LaterTextEditorSideState
): LaterExemplar => {
  const sequenceNumberGenerator = serialNumberGenerator();

  const left = getLaterSideFromEditorState(leftContent, 'left', sequenceNumberGenerator);
  const right = getLaterSideFromEditorState(rightContent, 'right', sequenceNumberGenerator);

  const leftImages = exemplar.content.left.filter(section => {
    return section.type !== 'section';
  });
  const rightImages = exemplar.content.right.filter(section => {
    return section.type !== 'section';
  });

  return {
    ...exemplar,
    content: {
      ...exemplar.content,
      left: [...left, ...leftImages],
      right: [...right, ...rightImages],
    },
  };
};
